import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import { Plus } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { StatItem } from '@/design/StatBand';
import { formatPct } from './PolicyFormatting';
import type { GovernanceRegisterViewModel } from './GovernanceRegisterViewModel';
import type { GovernancePosture } from './PolicyTypes';
import PolicyRegisterRow from './PolicyRegisterRow';

const MANAGE_ROUTE = '/classification/governance';

/** The Policy Register's posture headline stats — rendered in the Policies screen's summary rail. */
export function postureStats(posture: GovernancePosture): StatItem[] {
    return [
        { label: 'Policies', value: posture.policyCount.toLocaleString('en-US') },
        { label: 'Classified prompts', value: posture.totalTurns.toLocaleString(), hint: 'selected range' },
        { label: 'Avg confidence', value: posture.avgConfidence !== null ? formatPct(posture.avgConfidence) : '—' },
        { label: 'Flagged prompts', value: posture.flaggedFindings.toLocaleString(), hint: 'sensitivity flags' },
    ];
}

function EmptyState(): ReactElement {
    return (
        <SurfaceCard $padding="lg" style={{ alignItems: 'center', textAlign: 'center', gap: 'var(--mantine-spacing-sm)' }}>
            <Text fw={700}>No governance policies yet</Text>
            <Text c="dimmed" size="sm" maw={460}>
                Policies are governance-category classifications. Author one to start classifying AI activity against it.
            </Text>
            <Button component={Link} to={MANAGE_ROUTE} leftSection={<Plus size={16} />} mt="xs">
                Create a policy
            </Button>
        </SurfaceCard>
    );
}

/**
 * The Policy Register: a posture stat band over a vertical list of governance policies,
 * each showing its real classified activity. The {@link GovernanceRegisterViewModel} is
 * owned by the page shell and passed in.
 */
export default function PolicyRegister({ vm }: { vm: GovernanceRegisterViewModel }): ReactElement {
    if (!vm.loaded) {
        return (
            <Center py="xl">
                <Loader size="sm" />
            </Center>
        );
    }

    if (vm.errorMessage !== null) {
        return (
            <Text c="red" size="sm">
                Couldn't load governance data: {vm.errorMessage}
            </Text>
        );
    }

    if (vm.policies.length === 0) return <EmptyState />;

    return (
        <Stack gap="sm">
            {vm.policies.map((view) => (
                <PolicyRegisterRow key={view.policy.id} view={view} />
            ))}
        </Stack>
    );
}
